import {
  cmxDateTime,
  commonEnums,
  appServiceLines
} from '@codametrix/ui-common';
import React, { ReactElement } from "react";
import { PropertyAccessor } from '../case-prop-accessor/propery-accessor';
 import { Tooltip, Text } from "@chakra-ui/react";


const onlyUnique = (value, index, self) => {
  return self.indexOf(value) === index;
};

type DataTransformerFn = {
  [key: string]: (key: string) => ReactElement;
};

/**
 * DataTransformers take two objects.  A property accessor and the raw case run.
 * The raw case run is a fall back mechanism for array access which doesn't
 * currently have a method of highlighting.
 * @param propAccessor
 * @param rawCaseRun
 * @returns
 */
const DataTransformers = (
  propAccessor: PropertyAccessor,
  rawCaseRun: CMxAPI.CaseRun,
  activeContext?: CMxAPI.OrganizationContext
) => {
  return {
    friendly_date: (key: string) => {
      const { date_of_birth } = rawCaseRun;
      return date_of_birth && date_of_birth !== 'N/A'
        ? cmxDateTime.format(date_of_birth, cmxDateTime.FORMATS.BIRTH_DATE)
        : 'N/A';
    },

dob_age_by_dos: (key: string) => {
  const { date_of_birth, date_of_service } = rawCaseRun;

  if (!date_of_birth || !date_of_service || date_of_birth === "N/A") {
    return <Text>N/A</Text>;
  }

  const duration = cmxDateTime.calculateDurationDateFns(
    date_of_birth,
    date_of_service
  );

  // Handle invalid or future DOB vs DOS
  if (!duration || new Date(date_of_birth) > new Date(date_of_service)) {
    return <Text>N/A</Text>;
  }

  const formattedDOB = cmxDateTime.format(
    date_of_birth,
    cmxDateTime.FORMATS.BIRTH_DATE
  );

  let ageDisplay = "";
  if (duration.years && duration.years > 0) {
    ageDisplay = `${duration.years}y`;
  } else {
    ageDisplay = `${duration.months || 0}m ${duration.days || 0}d`;
  }

  return (
    <Text>
      {formattedDOB}{" "}
      <Tooltip
        label="at date of service"
        placement="top"
        hasArrow
      >
        <Text as="span" cursor="help" color="gray.700">
          ({ageDisplay})
        </Text>
      </Tooltip>
    </Text>
  );
},

    date_of_service: (key: string) => {
      const date = propAccessor(key);
      return date && typeof date === 'string' && date !== 'N/A'
        ? cmxDateTime.format(
            cmxDateTime.setTimeZoneForDate(rawCaseRun?.tenant_timezone, date),
            cmxDateTime.FORMATS.DATE_OF_SERVICE
          )
        : 'N/A';
    },
    visit_number: (key: string) => {
      return propAccessor(`${key}.number`);
    },
    primary_clinical_identifier: (key: string) => {
      return propAccessor(`${key}.number`);
    },
    mrn: (key: string) => {
      const numberProp = `${key}.number`;
      const sourceProp = `${key}.issuer`;
      return `${propAccessor(numberProp)} (src: ${propAccessor(sourceProp)})`;
    },
    numerical: (key: string) => {
      return propAccessor(`${key}.number`);
    },
    multi_place_of_service: (key: string) => {
      // to be unignored when caserun2 is implemented in omnibus.
      //@ts-ignore
      const invoices = rawCaseRun?.invoices || [];
      const place_of_service = invoices.map(
        invoice => invoice.place_of_service
      );
      const uniq_pos = place_of_service.filter(pos => pos).filter(onlyUnique);
      return uniq_pos.length
        ? uniq_pos.join(', ')
        : rawCaseRun.place_of_service || 'N/A';
    },
    specimen_sources: (key: string) => {
      // @ts-ignore
      const { specimen_sources } = rawCaseRun;
      if (!Array.isArray(specimen_sources)) {
        return specimen_sources;
      } else {
        const [first] = specimen_sources;
        if (first === undefined) {
          return 'N/A';
        }
        return first;
      }
    },
    identifier: (key: string) => {
      const numberProp = `${key}.number`;
      const sourceProp = `${key}.issuer`;
      return `${propAccessor(numberProp)} (src: ${propAccessor(sourceProp)})`;
    },
    primary_provider: (property: string) => {
      // Property is the role name
      const providers = propAccessor('providers');

      if ((!Array.isArray(providers) || providers.length === 0) || !providers) {
        // Fall back to billing provider name if no providers array
        return propAccessor('billing_provider_name') || 'N/A';
      }

      const providersByRole = providers.filter(
        provider => provider.provider_role === property
      );
      const providerNames = providersByRole.map(
        provider => provider.provider_name
      );

      if (!providerNames.length) {
        // Fall back to billing provider name if no primary provider found
        return propAccessor('billing_provider_name') || 'N/A';
      }
      return providerNames.join(' | ');
    },
    providers_by_role: (property: string) => {
      // Property can be a role name or an array of role names as a string
      const providers = propAccessor('providers');

      if (!Array.isArray(providers) || providers.length === 0) {
        return 'N/A';
      }

      let roles: string[] = [];

      // Check if property is an array string like "['First Assistant', 'Physician Assistant']"
      if (property.startsWith('[') && property.endsWith(']')) {
        try {
          roles = JSON.parse(property.replace(/'/g, '"'));
        } catch (e) {
          roles = [property];
        }
      } else {
        roles = [property];
      }

      const providersByRole = providers.filter(provider =>
        roles.includes(provider.provider_role)
      );

      const providerNames = providersByRole.map(
        provider => provider.provider_name
      );

      if (!providerNames.length) {
        return 'N/A';
      }

      return providerNames.join(' | ');
    },
    service_line: (key: string) => {
      const serviceLine = propAccessor(key);
      const mappedServiceLine = appServiceLines.isIPC(serviceLine)
        ? commonEnums.ServiceLines.BEDSIDE_PRO
        : serviceLine;

      if (activeContext) {
        const serviceLines = activeContext.serviceLines;
        const currentServiceLine = serviceLines.find(sl => {
          return sl.name === mappedServiceLine;
        });

        if (currentServiceLine) {
          return currentServiceLine.displayName || currentServiceLine.name;
        }
      }

      return appServiceLines.isIPC(serviceLine)
        ? commonEnums.ServiceLines.BEDSIDE_PRO
        : serviceLine;
    },
    age: (key: string) => {
      const { date_of_birth } = rawCaseRun;
      if (!date_of_birth || date_of_birth === 'N/A') {
        return 'N/A';
      }

      const birthDate = new Date(date_of_birth);
      const currentDate = new Date();
      let age = currentDate.getFullYear() - birthDate.getFullYear();
      const m = currentDate.getMonth() - birthDate.getMonth();

      if (m < 0 || (m === 0 && currentDate.getDate() < birthDate.getDate())) {
        age--;
      }

      return age.toString();
    },
    patient_age_by_dos_dob: (key: string) => {
      const patientAge = rawCaseRun.patient_age;
      if (!patientAge) {
        const dob = rawCaseRun.date_of_birth;
        const dos = rawCaseRun.date_of_service;

        if (dob && dos) {
          const dobDate = new Date(dob);
          const dosDate = new Date(dos);
          const age = dosDate.getFullYear() - dobDate.getFullYear();
          const m = dosDate.getMonth() - dobDate.getMonth();

          if (m < 0 || (m === 0 && dosDate.getDate() < dobDate.getDate())) {
            return age - 1;
          } else {
            return age;
          }
        }
      }
    },
    age_on_date_of_service: (key: string) => {
      const { date_of_birth } = rawCaseRun;
      const { date_of_service } = rawCaseRun;

      const duration = cmxDateTime.calculateDurationDateFns(
        date_of_birth,
        date_of_service
      );

      // conditional for bad data: negative time values occur when DOB is after DOS
      if (!duration || new Date(date_of_birth) > new Date(date_of_service)) {
        return 'N/A';
      }

      if (duration.years && duration.years > 0) {
        return `${duration.years}`;
      }

      return `${duration.months || 0} months, ${duration.days || 0} days`;
    }
  };
};

export { DataTransformers };
